/**
 * The reporting exports: the numbers a SETA or BBBEE submission is built from.
 *
 * Deliberately not four buttons and nothing else. Each card states what its
 * file contains and, more usefully, what it leaves out, in the same words the
 * workbook's own About sheet carries. Those exclusions are where a submission
 * goes wrong: an ATR that quietly included training nobody signed off is a
 * claim the company cannot defend, and the person pressing the button is the
 * last one who can catch it.
 */

import { createFileRoute } from "@tanstack/react-router"
import {
  BadgeCheck,
  CalendarClock,
  TimerReset,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react"
import * as React from "react"

import { CountUp } from "@/components/count-up"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Field,
  PageHeader,
  PickerInput,
  Select,
  Skeleton,
} from "@/components/ui"
import { ExportButton } from "@/features/exports/export-button"
import { useSummary, type Summary } from "@/features/reports/api"
import { BySiteCard } from "@/features/reports/by-site-card"
// One list, not a second copy that agrees with it today. The horizons were
// declared identically here and on /expiring; the expiring feature owns them
// now, since that is the screen the horizon is a first-class control on.
import { HORIZONS } from "@/features/reports/expiry-groups"
import { SpendBreakdown } from "@/features/reports/spend-breakdown"
import { StatCard } from "@/features/reports/stat-card"
import { formatRands } from "@/lib/utils"

export const Route = createFileRoute("/reports")({ component: ReportsScreen })

function ReportsScreen() {
  // One period across everything that takes one, which is now the spend
  // breakdown as well as the three feeders: a person compiling a submission is
  // working to a single reporting year, and four controls for one window is
  // four chances to set it differently.
  const [from, setFrom] = React.useState("")
  const [to, setTo] = React.useState("")
  const [days, setDays] = React.useState(90)
  const summary = useSummary()

  const period = { from, to }

  return (
    <>
      <PageHeader
        eyebrow="Insights"
        title="Reports"
        description="Where training performance stands, and the export files a SETA or BBBEE submission is built from."
      />

      {/* Same rule as the dashboard: the KPI strip replaces its skeletons
          silently, so one polite line says the figures are there. Outcome only,
          announced once. */}
      <p aria-live="polite" className="sr-only">
        {summary.isError
          ? "The report figures could not be loaded."
          : summary.data
            ? "Report figures loaded."
            : ""}
      </p>

      {summary.data ? (
        <>
          <Kpis data={summary.data} />
          <BySiteCard data={summary.data} className="mb-6" />
        </>
      ) : summary.isLoading ? (
        <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-28" />
          ))}
        </div>
      ) : null}

      {/* The period card sits above everything it governs, which is now the
          spend breakdown as well as the files. It used to sit under the
          exports heading below, which was true when the files were all it
          governed; a control below the thing it changes is the shape that
          teaches people it does not work. The KPI strip and the site bars
          above it are all-time figures and say so on their own captions, so
          nothing above this line silently claims to be filtered by it. */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Reporting period</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="From" htmlFor="from">
              <PickerInput
                id="from"
                type="date"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
              />
            </Field>
            <Field label="To" htmlFor="to">
              <PickerInput
                id="to"
                type="date"
                value={to}
                onChange={(event) => setTo(event.target.value)}
              />
            </Field>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Leave both empty for everything on record. Whatever is set here
            narrows the spend breakdown below and is written into each export
            file, so a figure on this screen and a spreadsheet downloaded
            beside it are the same claim about the same window.
          </p>
        </CardContent>
      </Card>

      <SpendBreakdown from={from} to={to} className="mb-6" />

      {/* "Feeder" is ours, not the client's: the term stays in the code and the
          docs, where it says something precise about the shape of these files,
          and stays out of the screen, where it is jargon standing between a
          person and the button they came for (ruled 2026-08-06). */}
      <h2 className="mb-4 mt-6 text-lg">
        Reporting exports
      </h2>

      <div className="grid gap-4 lg:grid-cols-2">
        <ReportCard
          icon={BadgeCheck}
          title="Annual Training Report (ATR)"
          subtitle="Training delivered"
          slug="atr"
          params={period}
          body="One row per learner per training event, for training that was attended and signed off on an event marked Completed. Each row carries the details a submission is broken down by: race, gender, disability, occupational level, OFO code and NQF level."
          excludes="Records nobody signed off, signed-off no-shows (vouched evidence the training was not delivered), and events that have not completed. Occupational level, job grade and OFO code are as at the training date; race, gender and disability are current values."
        />
        <ReportCard
          icon={CalendarClock}
          title="Workplace Skills Plan (WSP)"
          subtitle="Training planned"
          slug="wsp"
          params={period}
          body="One row per learner per training event that is Scheduled or Confirmed: training committed to but not yet delivered, with each learner's planned share of the cost."
          excludes="Drafts, which are somebody mid-capture rather than a plan, and cancelled events, which are training that will not happen."
        />
        <ReportCard
          icon={Wallet}
          title="Cost analysis"
          subtitle="What training cost"
          slug="cost-analysis"
          params={period}
          body="One row per completed event with the dimensions a cost review pivots by: training category, provider, business unit, region and date."
          excludes="Anything not yet completed, because a draft's costs are an estimate somebody is still editing. Sum the Total cost column, never the seven cost lines: those are the lump-sum breakdown and are unused on a per-person event."
        />
        <ReportCard
          icon={TimerReset}
          title="Expiring training"
          subtitle="What to book next"
          slug="expiry"
          params={{ days }}
          body="Signed-off training still in date but lapsing within the horizon, soonest first."
          excludes="Training that has already expired. This answers what to schedule, not what was missed."
          control={
            <Field label="Horizon" htmlFor="horizon">
              <Select
                id="horizon"
                value={String(days)}
                onChange={(event) => setDays(Number(event.target.value))}
              >
                {HORIZONS.map((horizon) => (
                  <option key={horizon.days} value={horizon.days}>
                    {horizon.label}
                  </option>
                ))}
              </Select>
            </Field>
          }
        />
      </div>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Why these are not SETA templates</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p>
            SETA templates change annually and differ between SETAs, so a
            replica of one is a document that is wrong next year. These files
            carry every dimension on every row instead, which pivots into
            whichever grid this year's template asks for.
          </p>
          <p>
            Every workbook has a second sheet recording what was measured, when,
            by whom and under which filters, so a number that reaches a
            submission can be traced back to what was actually counted.
          </p>
        </CardContent>
      </Card>
    </>
  )
}

/**
 * The headline figures, above the exports they summarise. The same endpoint
 * the dashboard reads, so the two screens can never disagree on a number.
 */
function Kpis({ data }: { data: Summary }) {
  const rate =
    data.events > 0 ? Math.round((data.events_completed / data.events) * 100) : null

  return (
    <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <StatCard
        label="Training spend"
        value={formatRands(data.spend, { cents: "auto" })}
        caption="Across completed events only"
        icon={Wallet}
        tone="brand"
      />
      <StatCard
        label="Completion rate"
        value={rate === null ? "Not yet" : <CountUp value={rate} format={(n) => `${n}%`} />}
        caption={
          rate === null
            ? "No events on the calendar yet"
            : `${data.events_completed} of ${data.events} events reached Completed`
        }
        icon={TrendingUp}
        tone="success"
      />
      <StatCard
        label="Learners trained"
        value={<CountUp value={data.learners_trained} />}
        caption="People with signed-off, attended training"
        icon={Users}
        tone="info"
      />
      <StatCard
        label="Cost per learner"
        value={
          data.spend_per_learner == null
            ? "Not yet"
            : formatRands(data.spend_per_learner, { cents: "auto" })
        }
        caption={
          data.spend_per_learner == null
            ? "Nobody has signed-off, attended training yet"
            : "Completed spend over learners trained"
        }
        icon={Wallet}
        tone="notice"
      />
    </div>
  )
}

function ReportCard({
  icon: Icon,
  title,
  subtitle,
  slug,
  params,
  body,
  excludes,
  control,
}: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  subtitle: string
  slug: string
  params: Record<string, string | number>
  body: string
  excludes: string
  control?: React.ReactNode
}) {
  return (
    <Card className="rise-in flex flex-col">
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {/* `rounded-lg` (15px), the same small-element radius StatCard's
              chips wear since 2026-08-04. It was `rounded-xl` (19px), which is
              the SURFACE radius: on a 40px box the browser scales a radius
              pair down when it exceeds the side, so this chip drew a shape
              neither 19px nor 15px while the identical chips one screen over
              drew 15px. One token, one shape. */}
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
            <Icon className="h-5 w-5" />
          </span>
          <div>
            <CardTitle>{title}</CardTitle>
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-3">
        <p className="text-sm">{body}</p>
        {/* 15px inside the 19px card that holds it: a nested panel at exactly
            its parent's radius reads as a mis-nesting, which is the same
            ruling the dashboard's quick actions carry. */}
        <div className="rounded-lg bg-surface/60 p-3">
          <p className="section-label">What it leaves out</p>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            {excludes}
          </p>
        </div>
        {control}
        {/* No label of its own. It said "Download Excel" here and "Export" on
            every list screen, which made one action wear two names; ruled
            2026-08-06 that a button downloading an export says "Export", so
            this takes the shared default rather than restating it. */}
        <div className="mt-auto flex justify-end pt-2">
          <ExportButton slug={slug} params={params} />
        </div>
      </CardContent>
    </Card>
  )
}
